import { Box, IconButton, TextField } from "@outschool/backpack";
import { faPaperPlane } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { useMutation } from "@outschool/ui-apollo";
import {
  AttachmentFragmentFragment,
  InlineEditableAttachments,
  PrivateClassMessageFragment,
  addPrivateClassMessageMutation,
} from "@outschool/ui-components-classroom";
import {
  AttachmentGallery,
  ErrorMessage,
} from "@outschool/ui-components-shared";
import { validOptimisticResponse } from "@outschool/ui-utils";
import React, { useState } from "react";

interface MessageInputProps {
  sectionUid: string;
  threadUid?: string;
  classPostUid?: string;
  learningAssignmentClassPostUid?: string;
  learnerUid: string;
  learnerName: string;
  onMessageSent: () => void;
  onThreadCreated?: (threadUid: string) => void;
}

export default function MessageInput({
  sectionUid,
  threadUid,
  classPostUid,
  learningAssignmentClassPostUid,
  learnerUid,
  learnerName,
  onMessageSent,
  onThreadCreated,
}: MessageInputProps) {
  const { t } = useTranslation(
    "learnerApp\\MessageTeacherButton\\MessageInput",
  );
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState<AttachmentFragmentFragment[]>(
    [],
  );
  const [video, setVideo] = useState<AttachmentFragmentFragment | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [addPrivateClassMessage] = useMutation(addPrivateClassMessageMutation);

  const hasContent =
    text.trim().length > 0 || attachments.length > 0 || video !== null;

  const handleDeleteAttachment = (
    attachment: { uid: string },
    _event: React.SyntheticEvent,
  ) => {
    if (video && video.uid === attachment.uid) {
      setVideo(null);
    } else {
      setAttachments(prev => prev.filter(a => a.uid !== attachment.uid));
    }
  };

  const handleSend = async () => {
    const trimmed = text.trim();
    if (!hasContent) {
      return;
    }
    setError(null);
    const previousText = text;
    const previousAttachments = attachments;
    const previousVideo = video;
    const attachmentUids = attachments.map(a => a.uid);
    const videoUid = video?.uid;
    setText("");
    setAttachments([]);
    setVideo(null);
    try {
      const result = await addPrivateClassMessage({
        variables: {
          privateClassMessageInput: {
            sectionUid,
            learnerUid,
            threadUid,
            classPostUid: threadUid ? undefined : classPostUid,
            learningAssignmentClassPostUid: threadUid
              ? undefined
              : learningAssignmentClassPostUid,
            messageContentInput: {
              text: trimmed,
              textMimeType: "TEXT_PLAIN",
              attachmentUids,
              videoUid,
            },
          },
        },
        optimisticResponse: threadUid
          ? validOptimisticResponse({
              __typename: "Mutation",
              addPrivateClassMessage: {
                __typename: "PrivateClassMessage",
                uid: `temp-${Date.now()}`,
                thread_uid: threadUid,
                sender: {
                  __typename: "MessageSender",
                  uid: learnerUid,
                  name: learnerName,
                  photo: null,
                  avatar: null,
                  isLearner: true,
                },
                messageContent: {
                  __typename: "MessageContent",
                  uid: `temp-content-${Date.now()}`,
                  text: trimmed,
                  htmlText: trimmed,
                  originalMimeType: "TEXT_PLAIN",
                  attachments: attachments.map(a => ({
                    __typename: "Attachment" as const,
                    ...a,
                  })),
                  video: video
                    ? { __typename: "Attachment" as const, ...video }
                    : null,
                  externalVideoUrl: null,
                  editedAt: null,
                },
                sentAt: new Date().toISOString(),
              },
            })
          : undefined,
        update(cache, { data: mutationData }) {
          if (!mutationData?.addPrivateClassMessage) {
            return;
          }
          const newMessage = mutationData.addPrivateClassMessage;
          const targetThreadUid = threadUid ?? newMessage.thread_uid;
          cache.modify({
            id: cache.identify({
              __typename: "BlockScopedThread",
              uid: targetThreadUid,
            }),
            fields: {
              messages(existing = []) {
                const newRef = cache.writeFragment({
                  fragment: PrivateClassMessageFragment,
                  fragmentName: "PrivateClassMessageFragment",
                  data: newMessage,
                });
                return [...existing, newRef];
              },
              latestMessage(existing) {
                return (
                  cache.writeFragment({
                    fragment: PrivateClassMessageFragment,
                    fragmentName: "PrivateClassMessageFragment",
                    data: newMessage,
                  }) ?? existing
                );
              },
            },
          });
        },
      });
      onMessageSent();
      const newThreadUid = result.data?.addPrivateClassMessage?.thread_uid;
      if (!threadUid && newThreadUid && onThreadCreated) {
        onThreadCreated(newThreadUid);
      }
    } catch (err) {
      setText(previousText);
      setAttachments(previousAttachments);
      setVideo(previousVideo);
      setError(err);
      OsPlatform.captureError(err);
    }
  };

  return (
    <Box
      sx={{
        borderTop: "1px solid",
        borderColor: "divider",
      }}
    >
      <ErrorMessage value={error} />
      {(attachments.length > 0 || video) && (
        <Box sx={{ padding: "1.2rem 1.6rem 0" }}>
          <AttachmentGallery
            video={video}
            attachments={attachments}
            onDelete={handleDeleteAttachment}
            containerSx={{ marginBottom: 0 }}
          />
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          gap: "0.8rem",
          padding: "1.6rem",
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            height: 40,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <InlineEditableAttachments
            showVideoUpload={true}
            video={video}
            value={attachments}
            onChange={setAttachments}
            onVideoChanged={setVideo}
            sx={{ flexShrink: 0, color: "text.secondary" }}
          />
        </Box>
        <TextField
          fullWidth
          multiline
          maxRows={4}
          size="small"
          placeholder={t("Message your teacher")}
          value={text}
          onChange={e => setText(e.target.value)}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "20px",
            },
          }}
        />
        <IconButton
          icon={faPaperPlane}
          aria-label={t("Send message")}
          onClick={handleSend}
          disabled={!hasContent}
          sx={{
            backgroundColor: "primary.main",
            color: "primary.contrastText",
            "&:hover": { backgroundColor: "primary.dark" },
            "&.Mui-disabled": {
              backgroundColor: "action.disabledBackground",
              color: "action.disabled",
            },
            width: 40,
            height: 40,
            flexShrink: 0,
          }}
        />
      </Box>
    </Box>
  );
}
